import { memo } from 'react';
import { css } from '@emotion/react';
import { useTranslation, i18next } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { Button, Text, Truncated, useTooltip } from '@frontend/design-system';

export type UnconfiguredReason = 'prompt' | 'dial-options' | 'prompt-and-dial-options';

const unconfiguredLabelMap: Record<UnconfiguredReason, string> = {
  prompt: i18next.t('(Missing Prompt)', { ns: 'phone' }),
  'dial-options': i18next.t('(Missing Dial Options)', { ns: 'phone' }),
  'prompt-and-dial-options': i18next.t('(Missing Prompt & Dial Options)', { ns: 'phone' }),
};

const unconfiguredTooltipTextMap: Record<UnconfiguredReason, string> = {
  prompt: i18next.t('To use this Phone Tree, it must have a prompt.', { ns: 'phone' }),
  'dial-options': i18next.t('To use this Phone Tree, it must have dial options.', { ns: 'phone' }),
  'prompt-and-dial-options': i18next.t('To use this Phone Tree, it must have a prompt and dial options.', {
    ns: 'phone',
  }),
};

export const PhoneTreeOptionRenderer = ({
  unconfiguredReason,
  isUnsaved = false,
  ...props
}: PhoneTreeOptionRendererProps) => {
  const { t } = useTranslation('phone');

  const unconfiguredLabel = unconfiguredReason ? unconfiguredLabelMap[unconfiguredReason] : undefined;

  const unconfiguredTooltipText = unconfiguredReason ? unconfiguredTooltipTextMap[unconfiguredReason] : undefined;

  if (unconfiguredLabel)
    return (
      <DisabledPhoneTreeOption
        {...props}
        label={unconfiguredLabel}
        tooltipText={unconfiguredTooltipText ?? ''}
        showTooltip={props.showUnconfiguredTooltip}
      />
    );
  if (isUnsaved)
    return props.showUnsavedTreeTooltip ? (
      <DisabledPhoneTreeOption
        {...props}
        label={t('(Unsaved)')}
        tooltipText={t('Save this Call Route to use this Phone Tree.')}
        showTooltip={props.showUnsavedTreeTooltip}
      />
    ) : (
      <SavedPhoneTreeOption {...props} label={t('(Unsaved)')} />
    );
  return <SavedPhoneTreeOption {...props} />;
};

// Shared component for rendering phone tree option UI.
// Branches into DisabledPhoneTreeOption or SavedPhoneTreeOption to avoid
// instantiating useTooltip for the common (saved) case.
interface PhoneTreeOptionRendererProps {
  name: string;
  subsequentPhoneTreeCount: number;
  isUnsaved?: boolean;
  showUnsavedTreeTooltip?: boolean;
  unconfiguredReason?: UnconfiguredReason;
  showUnconfiguredTooltip?: boolean;
  onViewClick: (e: React.MouseEvent) => void;
}

// No useTooltip — zero overhead for the common case
const SavedPhoneTreeOption = memo(
  ({
    name,
    subsequentPhoneTreeCount,
    label,
    onViewClick,
  }: Omit<PhoneTreeOptionRendererProps, 'isUnsaved' | 'showUnsavedTreeTooltip'> & { label?: string }) => {
    return (
      <div css={optionContainerStyle}>
        <Button
          variant='tertiary'
          iconName='view'
          onClick={(e) => {
            e.stopPropagation();
            onViewClick(e);
          }}
        />
        <span css={optionSpanStyle}>
          <Truncated css={truncatedStyle}>{name}</Truncated>
          <PhoneTreeCountSuffix count={subsequentPhoneTreeCount} />
          {label && (
            <Text as='span' color='subdued' css={labelTextStyle}>
              {label}
            </Text>
          )}
        </span>
      </div>
    );
  }
);
SavedPhoneTreeOption.displayName = 'SavedPhoneTreeOption';

// useTooltip only instantiated for disabled (unsaved/unconfigured) trees
const DisabledPhoneTreeOption = memo(
  ({
    name,
    subsequentPhoneTreeCount,
    label,
    tooltipText,
    showTooltip = true,
    onViewClick,
  }: {
    name: string;
    subsequentPhoneTreeCount: number;
    label: string;
    tooltipText: string;
    showTooltip?: boolean;
    onViewClick: (e: React.MouseEvent) => void;
  }) => {
    const { Tooltip, tooltipProps, triggerProps } = useTooltip({ showArrow: false });

    return (
      <div css={optionContainerStyle}>
        <Button
          variant='tertiary'
          iconName='view'
          css={disabledIconStyle}
          onClick={(e) => {
            e.stopPropagation();
            onViewClick(e);
          }}
        />
        <span css={optionSpanStyle}>
          <Truncated css={[truncatedStyle, disabledNameStyle]}>{name}</Truncated>
          <PhoneTreeCountSuffix count={subsequentPhoneTreeCount} />
          <Text as='span' css={disabledLabelTextStyle} {...triggerProps}>
            {label}
          </Text>
          {showTooltip && (
            <Tooltip {...tooltipProps}>
              <Text color='white'>{tooltipText}</Text>
            </Tooltip>
          )}
        </span>
      </div>
    );
  }
);
DisabledPhoneTreeOption.displayName = 'DisabledPhoneTreeOption';

// Shared component for rendering subsequent tree count suffix
const PhoneTreeCountSuffix = ({ count }: { count: number }) => {
  const { t } = useTranslation('phone');

  if (count === 0) return null;

  return (
    <Text as='span' color='subdued' css={countSuffixTextStyle}>
      {t(`+ {{count}} subsequent tree`, { count })}
    </Text>
  );
};

const countSuffixTextStyle = css`
  padding-left: ${theme.spacing(0.5)};
`;

const optionContainerStyle = css`
  display: flex;
  align-items: center;
`;

const optionSpanStyle = css`
  width: 89%;
  display: flex;
`;

const truncatedStyle = css`
  width: fit-content;
`;

const labelTextStyle = css`
  padding-left: ${theme.spacing(0.5)};
  font-style: italic;
`;

const disabledNameStyle = css`
  color: ${theme.font.colors.subdued};
`;

const disabledIconStyle = css`
  svg {
    color: ${theme.colors.primary30} !important;
  }
`;

const disabledLabelTextStyle = css`
  padding-left: ${theme.spacing(0.5)};
  font-style: italic;
  color: ${theme.font.colors.subdued};
`;
